export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function generateDiscountText(
  discounts: Array<{ discount: string }>,
): string | null {
  const pcts = discounts
    .map((pd) => pd.discount.match(/(\d+)\s*%/))
    .filter((m): m is RegExpMatchArray => m !== null)
    .map((m) => parseInt(m[1]!, 10));
  if (pcts.length === 0) return null;
  const max = Math.max(...pcts);
  return pcts.length === 1 ? `${max}% OFF` : `Hasta ${max}% OFF`;
}

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function formatTime(isoDate: string): string {
  return new Date(isoDate).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Returns initials from first and last name (e.g. "Juan Pérez" → "JP"), or "?" if empty. */
export function getInitials(firstName: string, lastName: string): string {
  const f = firstName.trim()[0] ?? "";
  const l = lastName.trim()[0] ?? "";
  return (f + l).toUpperCase() || "?";
}

export const formatDateWithoutTimeAndInWords = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const formatDateOnlyUTC = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

/** "31 ago 2026" — fecha corta en UTC, para strings YYYY-MM-DD sin corrimiento de día. */
export const formatDateShortUTC = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const formatDateWithTimeChile = (dateString: string) => {
  return new Date(dateString).toLocaleString("es-CL", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const INTERVAL_LABELS: Record<string, string> = {
  DAY: "Día",
  WEEK: "Semana",
  MONTH: "Mensual",
  QUARTER: "Trimestral",
  SEMESTER: "Semestral",
  YEAR: "Anual",
  CUSTOM: "Otro",
};

export const formatPrice = (price: number) => {
  const formattedPrice = Intl.NumberFormat("de-DE").format(price);
  return formattedPrice;
};

/** Trunca a entero (no redondea) y formatea con separadores de miles. Útil para mostrar montos de dinero sin decimales. */
export const formatPriceInt = (price: number): string =>
  Intl.NumberFormat("de-DE").format(Math.trunc(price));

export const formatNumber = (value: number, decimals = 0): string =>
  value.toLocaleString("es-CL", { maximumFractionDigits: decimals });

type StatusStyle = {
  label: string;
  classes: string;
};

/**
 * Trunca al día (sin hora) usando UTC para evitar el bug clásico de
 * timezone: las fechas del backend están guardadas como UTC midnight
 * (`YYYY-MM-DDT00:00:00.000Z`) representando el día-calendario. Si
 * truncamos con `getFullYear/Month/Date` (hora local del browser), en
 * timezones al oeste de UTC (Chile UTC-3/-4) la hora se interpreta como
 * "9 PM del día anterior" y el truncate colapsa la sub futura al mismo
 * día que "hoy", produciendo "0 días restantes" o "Expirado" un día
 * antes del que corresponde.
 *
 * Usando UTC consistentemente para `start`, `end` y `now`, los días-
 * calendario se comparan en el mismo frame y la diferencia es coherente.
 */
export function toDateOnly(d: string | Date) {
  const x = new Date(d);
  return new Date(
    Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate()),
  );
}

export function hasSubscriptionStarted(startDate: string | Date): boolean {
  const now = toDateOnly(new Date());
  const start = toDateOnly(startDate);
  return start <= now;
}

export function formatSubscriptionStatus(
  startDate: string | Date,
  endDate: string | Date,
): StatusStyle {
  const now = toDateOnly(new Date());
  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate);

  if (end < now) {
    return {
      label: "Expirado",
      classes: "bg-global-error text-global-error border",
    };
  }

  if (start > now) {
    return {
      label: "Próxima",
      classes:
        "bg-global-overlay-secondary text-global-label border border-global-border",
    };
  }

  return {
    label: "Activo",
    classes: "bg-global-success text-global-success border",
  };
}

export function getRemainingDaysLabel(endDate: string | Date): string {
  const now = toDateOnly(new Date());
  const end = toDateOnly(endDate);

  const diffMs = end.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return `${diffDays} día${diffDays > 1 ? "s" : ""} restantes`;
  } else if (diffDays === 0) {
    return "Último día";
  } else {
    return `${Math.abs(diffDays)} día${Math.abs(diffDays) > 1 ? "s" : ""} vencido${Math.abs(diffDays) > 1 ? "s" : ""}`;
  }
}

const PAYMENT_METHOD_LABELS_ES: Record<string, string> = {
  card: "Tarjeta",
  bank_transfer: "Transferencia Bancaria",
  cash: "Efectivo",
  mercado_pago: "Mercado Pago",
};

export const getPaymentMethodLabelEs = (method: string): string =>
  PAYMENT_METHOD_LABELS_ES[method] ?? method;

export const getPaymentMethodLabel = getPaymentMethodLabelEs;

const ACTION_LABELS_ES: Record<string, string> = {
  CREATE: "Crear",
  INSERT: "Creado",
  UPDATE: "Actualizar",
  DELETE: "Eliminar",
};

export const getActionLabelEs = (action: string): string =>
  ACTION_LABELS_ES[action] ?? action;

export const getActionLabel = getActionLabelEs;

/** "1 semana" o "3 semanas". El plural va explicito: el español no lo deriva. */
export const plural = (n: number, singular: string, pluralForm: string) =>
  `${n} ${n === 1 ? singular : pluralForm}`;
